import localforage from 'localforage';
import { STORAGE_KEYS } from '@/api/config';
import { clearAllStores } from '@/utils/storage/indexed-db-storage';
import {
  dropDatabase as dropTargetCacheDB,
} from '@/api/ctwa-ads-management/target-cache.service';
import {
  dropDatabase as dropPropertyCacheDB,
} from '@/api/property-management/property-management-cache.service';

/**
 * Known IndexedDB database names used by the application
 */
const KNOWN_INDEXED_DB_NAMES = [
  'notifyy', // Centralized IndexedDB storage
  'notifyy_target_cache', // Legacy target cache (for cleanup)
  'notifyy_property_management_cache', // Legacy property cache (for cleanup)
  'localforage', // Common library storage
  'keyval-store', // idb-keyval
] as const;

/**
 * Delete a specific IndexedDB database by name.
 * Always resolves — errors are logged but never propagated.
 */
function deleteIndexedDBByName(dbName: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(dbName);
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.warn(`[Auth] Failed to delete IndexedDB "${dbName}":`, request.error);
        resolve();
      };
      request.onblocked = () => {
        console.warn(`[Auth] IndexedDB "${dbName}" is blocked`);
        resolve();
      };
    } catch (error) {
      console.warn(`[Auth] Error deleting IndexedDB "${dbName}":`, error);
      resolve();
    }
  });
}

/**
 * Clear all localforage stores
 */
async function clearLocalForageStores(): Promise<void> {
  try {
    await clearAllStores().catch((e) =>
      console.warn('[Auth] Failed to clear centralized stores:', e)
    );
    await localforage.clear().catch((e) =>
      console.warn('[Auth] Failed to clear default localforage:', e)
    );
  } catch (error) {
    console.warn('[Auth] Error clearing localforage stores:', error);
  }
}

/**
 * Drop all localforage database instances
 */
async function dropLocalForageDatabases(): Promise<void> {
  try {
    await dropTargetCacheDB().catch((e) =>
      console.warn('[Auth] Failed to drop target cache DB:', e)
    );
    await dropPropertyCacheDB().catch((e) =>
      console.warn('[Auth] Failed to drop property cache DB:', e)
    );
    await localforage.dropInstance().catch((e) =>
      console.warn('[Auth] Failed to drop default localforage instance:', e)
    );
  } catch (error) {
    console.warn('[Auth] Error dropping localforage databases:', error);
  }
}

/**
 * Clear all IndexedDB databases
 */
async function clearIndexedDB(): Promise<void> {
  try {
    if (!window.indexedDB) return;

    await clearLocalForageStores();
    await dropLocalForageDatabases();

    await Promise.all(
      KNOWN_INDEXED_DB_NAMES.map((dbName) => deleteIndexedDBByName(dbName))
    );

    // Delete any unknown databases if the API is supported
    if ('databases' in indexedDB) {
      try {
        const databases = await indexedDB.databases();
        const knownSet = new Set<string>(KNOWN_INDEXED_DB_NAMES);
        await Promise.all(
          databases
            .filter((db) => db.name && !knownSet.has(db.name))
            .map((db) => deleteIndexedDBByName(db.name!))
        );
      } catch (error) {
        console.warn('[Auth] indexedDB.databases() not supported or failed:', error);
      }
    }
  } catch (error) {
    console.error('[Auth] Failed to clear IndexedDB:', error);
  }
}

/**
 * Clear all Cache Storage (Service Worker caches)
 */
async function clearCacheStorage(): Promise<void> {
  try {
    if (!('caches' in window)) return;
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  } catch (error) {
    console.error('[Auth] Failed to clear Cache Storage:', error);
  }
}

/**
 * Unregister all Service Workers
 */
async function unregisterServiceWorkers(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch (error) {
    console.error('[Auth] Failed to unregister Service Workers:', error);
  }
}

const AUTH_KEYS = [
  'persist:auth',
  STORAGE_KEYS.AUTH_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.USER_DATA,
  'auth_user',
  'loginResponse',
] as const;

const AUTH_COOKIES = ['auth_token', 'refresh_token', 'session_id'] as const;
const OAUTH_COOKIES = [
  'access_token',
  'accessToken',
  'refresh_token',
  'refreshToken',
  'id_token',
  'oauth_state',
  'oauth_nonce',
  'session_id',
  'JSESSIONID',
] as const;

export class CrossTabLogout {
  private static instance: CrossTabLogout | null = null;
  private channel: BroadcastChannel | null = null;
  private onLogout?: () => void;
  private storageHandler?: (e: StorageEvent) => void;
  private constructor() {
    this.initBroadcastChannel();
    this.initStorageFallback();
  }
  private initBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel('auth_logout');
      this.channel.onmessage = (event) => {
        if (event.data?.type === 'LOGOUT') {
          this.onLogout?.();
        }
      };
    } catch {
    }
  }

  private initStorageFallback(): void {
    this.storageHandler = (event: StorageEvent) => {
      if (event.key === 'logout_signal' && event.newValue === 'true') {
        this.onLogout?.();
        localStorage.removeItem('logout_signal');
      }
    };
    window.addEventListener('storage', this.storageHandler);
  }

  static getInstance(): CrossTabLogout {
    if (!CrossTabLogout.instance) {
      CrossTabLogout.instance = new CrossTabLogout();
    }
    return CrossTabLogout.instance;
  }

  setLogoutCallback(callback: () => void): void {
    this.onLogout = callback;
  }

  broadcastLogout(): void {
    try {
      this.channel?.postMessage({ type: 'LOGOUT' });
      localStorage.setItem('logout_signal', 'true');
    } catch (error) {
      console.error('[Auth] Failed to broadcast logout:', error);
    }
  }

  destroy(): void {
    this.channel?.close();
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
    }
    CrossTabLogout.instance = null;
  }
}

function clearCookie(name: string): void {
  const domain = window.location.hostname;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
}

/**
 * Clear all cookies currently set on the document
 */
function clearAllCookies(): void {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name) clearCookie(name);
  });
}

export function clearOAuthCookies(): void {
  const domain = window.location.hostname;
  const paths = ['/', '/auth', '/auth/callback'];
  OAUTH_COOKIES.forEach((cookieName) => {
    paths.forEach((path) => {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
      if (domain.includes('.')) {
        const parentDomain = domain.split('.').slice(-2).join('.');
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${parentDomain}`;
      }
    });
  });
}

export function clearAllStorage(): void {
  try {
    localStorage.clear();
    sessionStorage.clear();
    clearAllCookies();
  } catch (error) {
    console.error('[Auth] Failed to clear storage:', error);
  }
}

/**
 * Clear async site data: cookies, IndexedDB, CacheStorage, ServiceWorkers.
 * Does NOT clear localStorage/sessionStorage -- call clearWebStorage() for that.
 */
export async function clearAsyncSiteData(): Promise<void> {
  try {
    clearAllCookies();
    clearOAuthCookies();

    await Promise.all([
      clearIndexedDB(),
      clearCacheStorage(),
      unregisterServiceWorkers(),
    ]);
  } catch (error) {
    console.error('[Auth] Failed to clear async site data:', error);
  }
}

/**
 * Clear localStorage and sessionStorage.
 * Call this AFTER persistor.purge() to avoid race conditions.
 */
export function clearWebStorage(): void {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (error) {
    console.error('[Auth] Failed to clear web storage:', error);
  }
}

/**
 * Clear ALL site data including IndexedDB, Cache Storage, Service Workers.
 * NOTE: Prefer using clearAsyncSiteData() + clearWebStorage() separately
 * when persistor.purge() needs to run between them.
 */
export async function clearAllSiteData(): Promise<void> {
  try {
    clearWebStorage();
    await clearAsyncSiteData();
  } catch (error) {
    console.error('[Auth] Failed to clear all site data:', error);
  }
}

export function clearAuthStorage(): void {
  try {
    AUTH_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    AUTH_COOKIES.forEach(clearCookie);
  } catch (error) {
    console.error('[Auth] Failed to clear auth storage:', error);
  }
}

export function isAuthenticated(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch {
    return false;
  }
}

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  } catch (error) {
    console.error('[Auth] Failed to set token:', error);
  }
}

export function removeAuthToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('[Auth] Failed to remove token:', error);
  }
}

export type LogoutReason = 'manual' | 'timeout' | 'expired' | 'cross-tab' | 'error';
export interface LogoutOptions {
  showNotification?: boolean;
  reason?: LogoutReason;
  skipApi?: boolean;
  /** Bypass isInProgress guard on explicit retry */
  force?: boolean;
}
let logoutInProgress = false;
let crossTabNotificationShown = false;
let inProgressResetTimer: ReturnType<typeof setTimeout> | null = null;

const AUTO_RESET_TIMEOUT_MS = 5_000;

const PURGE_TIMEOUT_MS = 3_000;

export const LogoutService = {
  isInProgress(): boolean {
    return logoutInProgress;
  },
  setInProgress(value: boolean): void {
    logoutInProgress = value;

    if (inProgressResetTimer) {
      clearTimeout(inProgressResetTimer);
      inProgressResetTimer = null;
    }

    if (value) {
      inProgressResetTimer = setTimeout(() => {
        logoutInProgress = false;
        inProgressResetTimer = null;
      }, AUTO_RESET_TIMEOUT_MS);
    }
  },
  isCrossTabNotificationShown(): boolean {
    return crossTabNotificationShown;
  },
  setCrossTabNotificationShown(value: boolean): void {
    crossTabNotificationShown = value;
  },
  /**
   * Consolidated cleanup sequence that avoids the localStorage/persistor race condition.
   *
   * Order:
   * 1. Optionally broadcast cross-tab logout
   * 2. clearAsyncSiteData() — cookies, IndexedDB, CacheStorage, ServiceWorkers
   * 3. dispatch(forceLogout()) — clear Redux state
   * 4. persistor.purge() WITH timeout — clear persisted state from localStorage
   * 5. clearWebStorage() — NOW safe to clear localStorage/sessionStorage
   */
  async performFullCleanup(options: {
    dispatch: (action: unknown) => void;
    forceLogoutAction: () => { type: string };
    broadcast?: boolean;
  }): Promise<void> {
    const { dispatch, forceLogoutAction, broadcast = false } = options;

    try {
      if (broadcast) {
        try {
          CrossTabLogout.getInstance().broadcastLogout();
        } catch (e) {
          console.warn('[LogoutService] Cross-tab broadcast failed:', e);
        }
      }

      await clearAsyncSiteData();

      dispatch(forceLogoutAction());

      try {
        const { persistor } = await import("@/store");
        await Promise.race([
          persistor.purge(),
          new Promise<void>((_, reject) =>
            setTimeout(() => reject(new Error('persistor.purge() timed out')), PURGE_TIMEOUT_MS)
          ),
        ]);
      } catch (e) {
        console.warn('[LogoutService] persistor.purge() failed or timed out:', e);
      }

      clearWebStorage();
    } catch (error) {
      // Emergency fallback: force clear everything
      console.error('[LogoutService] Full cleanup error, forcing clear:', error);
      dispatch(forceLogoutAction());
      clearWebStorage();
    }
  },
  resetCrossTabNotificationAfterDelay(delayMs: number = 5000): void {
    setTimeout(() => {
      crossTabNotificationShown = false;
    }, delayMs);
  },
  reset(): void {
    logoutInProgress = false;
    crossTabNotificationShown = false;
  },
};

export default {
  CrossTabLogout,
  clearAllStorage,
  clearAllSiteData,
  clearAsyncSiteData,
  clearWebStorage,
  clearAuthStorage,
  clearOAuthCookies,
  isAuthenticated,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  LogoutService,
};
